/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Compact Print v1 — Ultra-Compact Decision Document + QR Animated Story
 *
 * Design philosophy:
 * - Print stays human (3-4 pages max)
 * - Depth stays optional (behind QR)
 * - Honesty stays visible
 * - Complexity is earned, not forced
 *
 * The printed artefact helps the customer DECIDE with confidence.
 * The QR-linked animated story provides depth for those who want it.
 *
 * Global print rules:
 *  ✅ Hard limit: 4 pages
 *  ✅ Two flagged options only (PRIMARY + SECONDARY)
 *  ✅ Simple cost & carbon comparison
 *  ✅ QR code links to animated story
 *  ❌ No detailed system behaviour explanations
 *  ❌ No repeated comfort graphs
 *  ❌ No technical appendices
 */

import type {
  SystemRecOutput,
  SystemRecInput,
  RecommendationOption,
  RecommendationDesignation,
} from './types';
import { RECOMMENDATION_DESIGNATIONS } from './types';
import { getOccupancyTimingLabel } from './occupancyUtils';
import {
  BehavioralArchetype,
  BEHAVIORAL_ARCHETYPES,
  ComfortCurvePoint,
} from './presentationPrint';

// ─── Types ───────────────────────────────────────────────────────

/**
 * QR code configuration for linking to animated story
 */
export interface QRCodeConfig {
  /** Base URL for the animated story (e.g., 'https://atlas-phm.uk/story') */
  baseUrl: string;
  /** Unique document identifier */
  documentId: string;
  /** Full URL to encode in QR */
  fullUrl: string;
  /** Size hint for QR code rendering (pixels) */
  sizeHint: number;
}

/**
 * Page 1: Context & Intent
 * "We understand your home"
 */
export interface ContextPage {
  /** Page title */
  title: string;
  /** Property summary (e.g., "3-bedroom semi-detached built 1970") */
  propertySummary: string;
  /** Usage summary (e.g., "Home most of day, 4 occupants") */
  usageSummary: string;
  /** What matters to this household (derived from experience questions) */
  whatMatters: string[];
  /** Credibility statement */
  credibilityStatement: string;
}

/**
 * A flagged option for Page 2
 * Stripped-down representation for decision-making
 */
export interface FlaggedOption {
  /** Designation (PRIMARY or SECONDARY) */
  designation: RecommendationDesignation;
  /** Display badge (e.g., "✅ Best overall advice" or "⚖️ Strong alternative") */
  badge: string;
  /** System name (e.g., "Combi Boiler with On-Demand Hot Water") */
  systemName: string;
  /** Behavioral archetype for this system */
  archetype: BehavioralArchetype;
  /** Comfort style (e.g., "Responsive & flexible" or "Steady & continuous") */
  comfortStyle: string;
  /** Typical annual cost (£/year) */
  typicalAnnualCost: number | null;
  /** Why this fits the household (single sentence) */
  whyThisFits: string;
  /** One honest caveat */
  caveat: string;
  /** Reference to original option */
  optionId: string;
}

/**
 * Page 2: The Two Flagged Options
 * "Here are your two sensible paths"
 */
export interface OptionsPage {
  /** Page title */
  title: string;
  /** Primary recommendation (best overall advice) */
  primary: FlaggedOption;
  /** Secondary recommendation (strong alternative) */
  secondary: FlaggedOption;
}

/**
 * A row in the cost & carbon comparison
 */
export interface ComparisonRow {
  /** Label (e.g., "Current system", "Option A", "Option B") */
  label: string;
  /** Energy used (kWh/year) */
  energyKwh: number | null;
  /** Annual cost (£/year) */
  annualCostGbp: number | null;
  /** Carbon (kgCO2/year) - only shown in QR story, not in print */
  carbonKg?: number | null;
}

/**
 * Page 3: Simple Cost & Carbon View
 * "What changes if you act"
 */
export interface ComparisonPage {
  /** Page title */
  title: string;
  /** Current system (baseline) */
  current: ComparisonRow;
  /** Option A (primary recommendation) */
  optionA: ComparisonRow;
  /** Option B (secondary recommendation) */
  optionB: ComparisonRow;
  /** Footnote about estimates */
  footnote: string;
}

/**
 * A next step action
 */
export interface NextStep {
  /** Icon (e.g., "1", "2", "3") */
  number: number;
  /** Action text */
  action: string;
}

/**
 * Page 4: Next Steps + QR
 * "What now?"
 */
export interface NextStepsPage {
  /** Page title */
  title: string;
  /** Next steps */
  steps: NextStep[];
  /** QR code configuration */
  qrCode: QRCodeConfig;
  /** Text above QR code */
  qrPrompt: string;
}

/**
 * Complete compact print data (3-4 pages)
 */
export interface CompactPrintData {
  /** Unique document identifier */
  documentId: string;
  /** Generation timestamp */
  generatedAt: string;
  /** Page 1: Context & Intent */
  context: ContextPage;
  /** Page 2: Two Flagged Options */
  options: OptionsPage;
  /** Page 3: Cost Comparison */
  comparison: ComparisonPage;
  /** Page 4: Next Steps + QR */
  nextSteps: NextStepsPage;
}

// ─── Animated Story Types ────────────────────────────────────────

/**
 * Story beat types for the animated experience
 */
export type StoryBeatType =
  | 'home_at_glance'      // Beat 1: Your home, at a glance
  | 'what_stays_same'     // Beat 2: What stays the same
  | 'what_changes'        // Beat 3: What changes
  | 'option_comparison'   // Beat 4: Option A vs Option B
  | 'honesty_moment'      // Beat 5: When this wouldn't be right
  | 'long_term_view'      // Beat 6: The long-term view
  | 'decision_screen';    // Beat 7: Decision screen

/**
 * Beat 1: Your home, at a glance
 */
export interface HomeAtGlanceBeat {
  type: 'home_at_glance';
  title: string;
  /** Simple house graphic data */
  houseVisual: {
    propertyType: string;
    bedrooms: number;
    bathrooms: number;
  };
  /** Usage profile highlights */
  usageProfile: string[];
  /** What matters (highlighted) */
  whatMattersHighlights: string[];
}

/**
 * Beat 2: What stays the same
 */
export interface WhatStaysSameBeat {
  type: 'what_stays_same';
  title: string;
  /** Things that don't change */
  unchanged: string[];
}

/**
 * Beat 3: What changes
 */
export interface WhatChangesBeat {
  type: 'what_changes';
  title: string;
  /** Energy in direction */
  energyDirection: 'down' | 'stable' | 'up';
  /** Stress on system direction */
  stressDirection: 'down' | 'stable' | 'up';
  /** Costs direction */
  costsDirection: 'down' | 'stable' | 'up';
  /** Specific changes with values */
  changes: Array<{
    label: string;
    before: string;
    after: string;
    direction: 'better' | 'same' | 'tradeoff';
  }>;
}

/**
 * Beat 4: Option A vs Option B
 */
export interface OptionComparisonBeat {
  type: 'option_comparison';
  title: string;
  /** Option A details */
  optionA: {
    name: string;
    archetype: BehavioralArchetype;
    curve: ComfortCurvePoint[];
    bullets: string[];
  };
  /** Option B details */
  optionB: {
    name: string;
    archetype: BehavioralArchetype;
    curve: ComfortCurvePoint[];
    bullets: string[];
  };
}

/**
 * Beat 5: Honesty moment
 */
export interface HonestyMomentBeat {
  type: 'honesty_moment';
  title: string;
  /** When this advice wouldn't be right */
  caveats: string[];
  /** Specific conditions that might change the advice */
  conditions: string[];
}

/**
 * Beat 6: The long-term view
 */
export interface LongTermViewBeat {
  type: 'long_term_view';
  title: string;
  /** Clean systems benefit */
  cleanSystems: string;
  /** Modulation benefit */
  modulation: string;
  /** Longevity benefit */
  longevity: string;
  /** Educational bullets (from appendices) */
  educationalBullets: string[];
}

/**
 * Decision action for Beat 7
 */
export interface DecisionAction {
  /** Action label */
  label: string;
  /** Action type */
  type: 'select_a' | 'select_b' | 'not_ready' | 'speak_to_advisor';
  /** Whether this is a primary action */
  primary: boolean;
}

/**
 * Beat 7: Decision screen
 */
export interface DecisionScreenBeat {
  type: 'decision_screen';
  title: string;
  /** Available actions */
  actions: DecisionAction[];
}

/**
 * Union type for all story beats
 */
export type StoryBeat =
  | HomeAtGlanceBeat
  | WhatStaysSameBeat
  | WhatChangesBeat
  | OptionComparisonBeat
  | HonestyMomentBeat
  | LongTermViewBeat
  | DecisionScreenBeat;

/**
 * Complete animated story data for QR-linked experience
 */
export interface AnimatedStoryData {
  /** Unique document identifier (matches print document) */
  documentId: string;
  /** Generation timestamp */
  generatedAt: string;
  /** Story beats in order */
  beats: StoryBeat[];
  /** Full recommendation data for deep dives */
  recommendations: {
    primary: RecommendationOption;
    secondary: RecommendationOption;
  };
  /** Baseline data for comparisons */
  baseline?: {
    annualCostGbp: number;
    annualEnergyKwh: number;
    annualCarbonKg: number;
  };
}

// ─── Helper Functions ────────────────────────────────────────────

/**
 * Generate a unique document ID.
 * Uses crypto.randomUUID when available for stronger uniqueness,
 * falling back to timestamp+random. Each call ALWAYS returns a new
 * ID — never derived from input data or fingerprints.
 */
function generateDocumentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `doc_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
  }
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `doc_${timestamp}_${random}`;
}

/**
 * Generate QR code configuration
 */
function generateQRConfig(documentId: string, baseUrl: string = 'https://atlas-phm.uk/story/interactive'): QRCodeConfig {
  const fullUrl = `${baseUrl}/${documentId}`;
  return {
    baseUrl,
    documentId,
    fullUrl,
    sizeHint: 150,
  };
}

/**
 * Determine behavioral archetype for an option
 */
function getArchetype(option: RecommendationOption): BehavioralArchetype {
  const st = option.systemType?.toLowerCase() || '';
  const b = option.boiler?.toLowerCase() || '';

  if (st.includes('heat_pump') || b.includes('heat_pump') || b.includes('ashp')) {
    return 'STEADY_CONTINUOUS';
  }

  // Gas boilers are responsive
  return 'RESPONSIVE_BURST';
}

/**
 * Get comfort style description for an option
 */
function getComfortStyle(option: RecommendationOption): string {
  const archetype = getArchetype(option);
  return BEHAVIORAL_ARCHETYPES[archetype].summary;
}

/**
 * Generate "why this fits" sentence
 */
function generateWhyThisFits(option: RecommendationOption, input: SystemRecInput): string {
  const archetype = getArchetype(option);
  const occupancy = input.occupancyTiming || 'unknown';

  if (archetype === 'STEADY_CONTINUOUS') {
    if (occupancy === 'most_of_day') {
      return 'Suits your daytime occupancy with steady, consistent warmth throughout the day.';
    }
    return 'Provides very stable comfort once warm, ideal for predictable routines.';
  }

  // RESPONSIVE_BURST
  if (occupancy === 'evenings_weekends' || occupancy === 'mornings_evenings') {
    return 'Quick to warm up when you need it, flexible for your varied routine.';
  }
  return 'Fast response and flexible operation to match your lifestyle.';
}

/**
 * Generate honest caveat for an option
 */
function generateCaveat(option: RecommendationOption): string {
  const archetype = getArchetype(option);

  if (archetype === 'STEADY_CONTINUOUS') {
    return 'Works best with good insulation and larger radiators; may need radiator upgrades.';
  }

  // Gas systems
  const water = option.water?.toLowerCase() || '';
  if (water.includes('on_demand')) {
    return 'One outlet at a time for hot water; flow rate depends on mains pressure.';
  }

  return 'Requires space for a hot water cylinder if not already present.';
}

/**
 * Build "what matters" list from experience questions
 */
function buildWhatMatters(input: SystemRecInput): string[] {
  const matters: string[] = [];
  const exp = input.experienceQuestions;

  if (!exp) {
    return ['Comfortable heating', 'Reliable hot water', 'Reasonable running costs'];
  }

  if (exp.simultaneousHotWater === 'most_days') {
    matters.push('Multiple hot water outlets at once');
  }
  if (exp.operatingStyle === 'quick_response') {
    matters.push('Quick heat response');
  } else if (exp.operatingStyle === 'steady_consistent') {
    matters.push('Steady, consistent warmth');
  }
  if (exp.noiseSensitivity === 'very_sensitive') {
    matters.push('Quiet operation');
  }
  if (exp.environmentalIntent === 'greenest_now') {
    matters.push('Environmental impact');
  }
  if (exp.spacePressure === 'very_tight') {
    matters.push('Minimal space requirements');
  }

  // Ensure we have at least some items
  if (matters.length === 0) {
    matters.push('Comfortable heating', 'Reliable hot water');
  }

  return matters;
}

/**
 * Get system name from option
 */
function getSystemName(option: RecommendationOption): string {
  const parts: string[] = [];
  if (option.boilerLabel) parts.push(option.boilerLabel);
  if (option.waterLabel) parts.push(`with ${option.waterLabel.toLowerCase()}`);
  if (parts.length > 0) return parts.join(' ');
  return option.title;
}

// ─── Label Helpers ───────────────────────────────────────────────

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  flat: 'flat',
  maisonette: 'maisonette',
  terraced: 'terraced house',
  mid_terrace: 'mid-terrace house',
  end_terrace: 'end-terrace house',
  semi: 'semi-detached house',
  semi_detached: 'semi-detached house',
  detached: 'detached house',
  bungalow: 'bungalow',
  semi_detached_bungalow: 'semi-detached bungalow',
};

const AGE_BAND_LABELS: Record<string, string> = {
  pre_1919: 'pre-1919',
  '1919_1944': '1920s-1940s',
  '1945_1964': '1950s-1960s',
  '1965_1980': '1970s',
  '1981_2000': '1980s-1990s',
  '2001_2010': '2000s',
  '2011_plus': 'post-2011',
};

// ─── Comfort Curves ──────────────────────────────────────────────

function heatPumpComfortCurve(): ComfortCurvePoint[] {
  return [
    { hour: 0, comfort: 30 },
    { hour: 1, comfort: 38 },
    { hour: 2, comfort: 48 },
    { hour: 3, comfort: 58 },
    { hour: 4, comfort: 66 },
    { hour: 5, comfort: 73 },
    { hour: 6, comfort: 79 },
    { hour: 7, comfort: 84 },
    { hour: 8, comfort: 88 },
    { hour: 10, comfort: 92 },
    { hour: 12, comfort: 94 },
    { hour: 16, comfort: 95 },
    { hour: 20, comfort: 96 },
    { hour: 24, comfort: 96 },
  ];
}

function gasBoilerComfortCurve(): ComfortCurvePoint[] {
  return [
    { hour: 0, comfort: 30 },
    { hour: 0.5, comfort: 50 },
    { hour: 1, comfort: 68 },
    { hour: 1.5, comfort: 78 },
    { hour: 2, comfort: 85 },
    { hour: 3, comfort: 90 },
    { hour: 4, comfort: 92 },
    { hour: 6, comfort: 93 },
    { hour: 8, comfort: 93 },
    { hour: 12, comfort: 93 },
    { hour: 16, comfort: 93 },
    { hour: 20, comfort: 93 },
    { hour: 24, comfort: 93 },
  ];
}

function getComfortCurve(option: RecommendationOption): ComfortCurvePoint[] {
  const archetype = getArchetype(option);
  return archetype === 'STEADY_CONTINUOUS' ? heatPumpComfortCurve() : gasBoilerComfortCurve();
}

function getComfortBullets(option: RecommendationOption): string[] {
  const archetype = getArchetype(option);

  if (archetype === 'STEADY_CONTINUOUS') {
    return [
      'Slow, steady warm-up from cold',
      'Very stable comfort once warm',
      'Best suited to continuous or predictable heating',
    ];
  }

  return [
    'Fast warm-up and recovery',
    'Stable comfort with good controls',
    'Flexible for changing routines',
  ];
}

// ─── Main Builder Functions ──────────────────────────────────────

/**
 * Build compact print data from engine output.
 *
 * Creates a 3-4 page decision document:
 * - Page 1: Context & Intent
 * - Page 2: Two Flagged Options
 * - Page 3: Cost Comparison
 * - Page 4: Next Steps + QR
 */
export function buildCompactPrint(
  output: SystemRecOutput,
  input: SystemRecInput,
  storyBaseUrl?: string
): CompactPrintData {
  const documentId = generateDocumentId();
  const qrConfig = generateQRConfig(documentId, storyBaseUrl);

  // Get primary and secondary recommendations
  const primary = output.primary;
  const secondary = output.alternatives?.[0];

  if (!primary || !secondary) {
    throw new Error('Both primary and secondary recommendations are required for compact print');
  }

  // Assign designations
  primary.designation = RECOMMENDATION_DESIGNATIONS.PRIMARY;
  secondary.designation = RECOMMENDATION_DESIGNATIONS.SECONDARY;

  // ── Page 1: Context ──
  const propertyType = PROPERTY_TYPE_LABELS[input.propertyType || ''] || 'home';
  const ageBand = AGE_BAND_LABELS[input.propertyAgeBand || ''] || '';
  const bedrooms = input.bedrooms ?? 3;
  const bathrooms = input.bathrooms ?? 1;
  const occupancy = input.occupancy ?? 2;
  const occupancyLabel = getOccupancyTimingLabel(input.occupancyTiming);

  const propertySummary = ageBand
    ? `${bedrooms}-bedroom ${propertyType} built ${ageBand}`
    : `${bedrooms}-bedroom ${propertyType}`;

  const usageSummary = `${occupancyLabel}, ${occupancy} occupant${occupancy !== 1 ? 's' : ''}`;

  const context: ContextPage = {
    title: 'Your Home',
    propertySummary,
    usageSummary,
    whatMatters: buildWhatMatters(input),
    credibilityStatement: 'This advice balances comfort, running cost, reliability, and future flexibility — without assuming major changes.',
  };

  // ── Page 2: Options ──
  const primaryAnnualCost = output.year1?.options?.find(o => o.optionId === primary.id)?.impact.costGbp
    ?? primary.deltaVsBaseline?.optionAnnualized_gbp
    ?? null;

  const secondaryAnnualCost = output.year1?.options?.find(o => o.optionId === secondary.id)?.impact.costGbp
    ?? secondary.deltaVsBaseline?.optionAnnualized_gbp
    ?? null;

  const options: OptionsPage = {
    title: 'Your Two Options',
    primary: {
      designation: RECOMMENDATION_DESIGNATIONS.PRIMARY,
      badge: 'Best overall advice',
      systemName: getSystemName(primary),
      archetype: getArchetype(primary),
      comfortStyle: getComfortStyle(primary),
      typicalAnnualCost: primaryAnnualCost,
      whyThisFits: generateWhyThisFits(primary, input),
      caveat: generateCaveat(primary),
      optionId: primary.id,
    },
    secondary: {
      designation: RECOMMENDATION_DESIGNATIONS.SECONDARY,
      badge: 'Strong alternative',
      systemName: getSystemName(secondary),
      archetype: getArchetype(secondary),
      comfortStyle: getComfortStyle(secondary),
      typicalAnnualCost: secondaryAnnualCost,
      whyThisFits: generateWhyThisFits(secondary, input),
      caveat: generateCaveat(secondary),
      optionId: secondary.id,
    },
  };

  // ── Page 3: Comparison ──
  const baselineCost = output.year1?.baseline?.costGbp ?? null;
  const baselineEnergy = output.year1?.baseline?.totalHeatKwh ?? null;

  const primaryEnergy = output.year1?.options?.find(o => o.optionId === primary.id)?.impact.totalHeatKwh ?? null;
  const secondaryEnergy = output.year1?.options?.find(o => o.optionId === secondary.id)?.impact.totalHeatKwh ?? null;

  const comparison: ComparisonPage = {
    title: 'What Changes',
    current: {
      label: 'Current system',
      energyKwh: baselineEnergy,
      annualCostGbp: baselineCost,
    },
    optionA: {
      label: 'Option A',
      energyKwh: primaryEnergy,
      annualCostGbp: primaryAnnualCost,
    },
    optionB: {
      label: 'Option B',
      energyKwh: secondaryEnergy,
      annualCostGbp: secondaryAnnualCost,
    },
    footnote: 'Estimates based on your home size, insulation, and typical usage patterns.',
  };

  // ── Page 4: Next Steps ──
  const nextSteps: NextStepsPage = {
    title: 'What Now?',
    steps: [
      { number: 1, action: 'Choose Option A or Option B' },
      { number: 2, action: 'Speak to your advisor to confirm details' },
      { number: 3, action: 'Or scan the QR code to explore more' },
    ],
    qrCode: qrConfig,
    qrPrompt: 'Scan to see how these systems work in detail',
  };

  return {
    documentId,
    generatedAt: new Date().toISOString(),
    context,
    options,
    comparison,
    nextSteps,
  };
}

/**
 * Build animated story data for QR-linked experience.
 *
 * Creates a 7-beat scroll/slide-driven story:
 * 1. "Your home, at a glance"
 * 2. "What stays the same"
 * 3. "What changes"
 * 4. Option A vs Option B
 * 5. Honesty moment
 * 6. The long-term view
 * 7. Decision screen
 */
export function buildAnimatedStory(
  output: SystemRecOutput,
  input: SystemRecInput,
  documentId?: string
): AnimatedStoryData {
  const docId = documentId || generateDocumentId();

  const primary = output.primary;
  const secondary = output.alternatives?.[0];

  if (!primary || !secondary) {
    throw new Error('Both primary and secondary recommendations are required for animated story');
  }

  // Assign designations
  primary.designation = RECOMMENDATION_DESIGNATIONS.PRIMARY;
  secondary.designation = RECOMMENDATION_DESIGNATIONS.SECONDARY;

  const beats: StoryBeat[] = [];

  // ── Beat 1: Home at Glance ──
  const propertyType = PROPERTY_TYPE_LABELS[input.propertyType || ''] || 'home';
  const occupancyLabel = getOccupancyTimingLabel(input.occupancyTiming);

  const homeAtGlance: HomeAtGlanceBeat = {
    type: 'home_at_glance',
    title: 'Your Home, At a Glance',
    houseVisual: {
      propertyType,
      bedrooms: input.bedrooms ?? 3,
      bathrooms: input.bathrooms ?? 1,
    },
    usageProfile: [
      occupancyLabel,
      `${input.occupancy ?? 2} people`,
      input.hotWaterDemand ? `${input.hotWaterDemand} hot water demand` : 'Typical hot water usage',
    ],
    whatMattersHighlights: buildWhatMatters(input),
  };
  beats.push(homeAtGlance);

  // ── Beat 2: What Stays Same ──
  const whatStaysSame: WhatStaysSameBeat = {
    type: 'what_stays_same',
    title: 'What Stays the Same',
    unchanged: [
      'Same comfort levels',
      'Same hot water availability',
      'Same lifestyle and routines',
      'No structural changes to your home',
    ],
  };
  beats.push(whatStaysSame);

  // ── Beat 3: What Changes ──
  const baselineCost = output.year1?.baseline?.costGbp ?? null;
  const primaryCost = output.year1?.options?.find(o => o.optionId === primary.id)?.impact.costGbp ?? null;

  const costSaving = baselineCost && primaryCost ? baselineCost - primaryCost : 0;
  const costsDirection = costSaving > 50 ? 'down' : costSaving < -50 ? 'up' : 'stable';

  const whatChanges: WhatChangesBeat = {
    type: 'what_changes',
    title: 'What Changes',
    energyDirection: 'down', // New systems are typically more efficient
    stressDirection: 'down', // New systems have less wear
    costsDirection,
    changes: [
      {
        label: 'Annual running cost',
        before: baselineCost ? `£${Math.round(baselineCost)}` : '—',
        after: primaryCost ? `£${Math.round(primaryCost)}` : '—',
        direction: costSaving > 0 ? 'better' : 'tradeoff',
      },
      {
        label: 'System reliability',
        before: 'Aging system',
        after: 'New, warranted equipment',
        direction: 'better',
      },
      {
        label: 'Control precision',
        before: 'Basic controls',
        after: 'Modern modulating controls',
        direction: 'better',
      },
    ],
  };
  beats.push(whatChanges);

  // ── Beat 4: Option Comparison ──
  const optionComparison: OptionComparisonBeat = {
    type: 'option_comparison',
    title: 'Option A vs Option B',
    optionA: {
      name: getSystemName(primary),
      archetype: getArchetype(primary),
      curve: getComfortCurve(primary),
      bullets: getComfortBullets(primary),
    },
    optionB: {
      name: getSystemName(secondary),
      archetype: getArchetype(secondary),
      curve: getComfortCurve(secondary),
      bullets: getComfortBullets(secondary),
    },
  };
  beats.push(optionComparison);

  // ── Beat 5: Honesty Moment ──
  const caveats: string[] = [];
  const conditions: string[] = [];

  // Add heat pump caveats if applicable
  if (getArchetype(primary) === 'STEADY_CONTINUOUS' || getArchetype(secondary) === 'STEADY_CONTINUOUS') {
    caveats.push('Heat pumps work best with good insulation and may need radiator upgrades');
    conditions.push('Homes with poor insulation may not achieve expected efficiency');
    conditions.push('Very small radiators may need replacing for low-temperature operation');
  }

  // Add general caveats
  caveats.push('All estimates assume typical usage patterns');
  conditions.push('Actual costs will vary with energy prices and how you use your home');
  conditions.push('Installation complexity may vary depending on existing pipework');

  const honestyMoment: HonestyMomentBeat = {
    type: 'honesty_moment',
    title: 'When This Wouldn\'t Be Right',
    caveats,
    conditions,
  };
  beats.push(honestyMoment);

  // ── Beat 6: Long-Term View ──
  const longTermView: LongTermViewBeat = {
    type: 'long_term_view',
    title: 'The Long-Term View',
    cleanSystems: 'Clean water allows full modulation, reducing mechanical stress and extending component life.',
    modulation: 'Modern boilers running gently for longer are more efficient than firing at full power in short bursts.',
    longevity: 'Well-designed systems with proper controls typically last 15-20 years with proper maintenance.',
    educationalBullets: [
      'Larger pipes carry more water at lower velocity, meaning less turbulence and gentler heat delivery',
      'Load compensation measures the gap between room and setpoint, telling the boiler exactly how much heat to produce',
      'Oversized radiators work at lower flow temperatures, which is exactly what condensing boilers need',
      'Steady operation reduces stress and improves efficiency compared to frequent on/off cycling',
    ],
  };
  beats.push(longTermView);

  // ── Beat 7: Decision Screen ──
  const decisionScreen: DecisionScreenBeat = {
    type: 'decision_screen',
    title: 'Your Decision',
    actions: [
      { label: 'I want Option A', type: 'select_a', primary: true },
      { label: 'I want Option B', type: 'select_b', primary: false },
      { label: 'I\'m not ready yet', type: 'not_ready', primary: false },
      { label: 'Speak to an advisor', type: 'speak_to_advisor', primary: false },
    ],
  };
  beats.push(decisionScreen);

  // ── Baseline data ──
  const baseline = output.year1?.baseline ? {
    annualCostGbp: output.year1.baseline.costGbp,
    annualEnergyKwh: output.year1.baseline.totalHeatKwh,
    annualCarbonKg: output.year1.baseline.carbonKg,
  } : undefined;

  return {
    documentId: docId,
    generatedAt: new Date().toISOString(),
    beats,
    recommendations: {
      primary,
      secondary,
    },
    baseline,
  };
}

/**
 * Build both compact print and animated story from engine output.
 * Ensures both share the same document ID for linking.
 */
export function buildCompactPrintWithStory(
  output: SystemRecOutput,
  input: SystemRecInput,
  storyBaseUrl?: string
): { print: CompactPrintData; story: AnimatedStoryData } {
  const print = buildCompactPrint(output, input, storyBaseUrl);
  const story = buildAnimatedStory(output, input, print.documentId);

  return { print, story };
}
